import { disabilityFactsFromJson, disabilityFactsToJson, needsDisabilityFacts } from '../disabilityFacts';
import { API_BASE_URL } from '../../../../../config';
import type { FamilyMemberApi, FamilyMemberDraft } from './familyInfoTypes';
import {
  deriveDependentTaxEligible,
  incomeOverDependentLimit,
  isNonSpouseTaxFlow,
  isUnder16AtTaxYearEnd,
} from './familyInfoDependentEligibility';
import { deriveSpouseWithholding, isSpouseTaxFlow } from './familyInfoSpouseEligibility';

export type FamilyInfoProgressResponse = {
  email?: string;
  invitationId?: string;
  familyInfoStatus?: string;
  familyInfoStep?: string;
  draft?: FamilyMemberApi | null;
  members?: FamilyMemberApi[];
};

function draftToApi(d: FamilyMemberDraft): FamilyMemberApi {
  const str = (v: string | null | undefined) => (typeof v === 'string' ? v : '').trim();
  const yes = (v: string) => v === 'yes';
  const sameHousehold = d.livingInSameHousehold === 'yes';
  const notSameHousehold = d.livingInSameHousehold === 'no';

  const claimTax = d.claimTaxDependent === 'yes' || d.claimTaxDependent === 'no' ? d.claimTaxDependent : '';
  const ineligibleReason = str(d.taxIneligibilityReason);
  const isNonSpouseTax = d.relationship !== 'spouse' && claimTax === 'yes';

  // Ineligible early-exit members may have lost sharedLivelihood in older rows — re-assert it.
  let sharedLivelihood: boolean | undefined =
    d.sharedLivelihood === '' ? undefined : yes(d.sharedLivelihood);
  if (isNonSpouseTax && ineligibleReason === 'no_shared_livelihood') {
    sharedLivelihood = false;
  }

  let nonResidentFlag: boolean | undefined;
  if (notSameHousehold) {
    if (d.relationship !== 'spouse' && claimTax === 'yes') {
      if (d.taxNonResident === 'yes') nonResidentFlag = true;
      else if (d.taxNonResident === 'no') nonResidentFlag = false;
      else nonResidentFlag = undefined;
    } else if (d.outsideJapan === 'yes') nonResidentFlag = true;
    else if (d.outsideJapan === 'no') nonResidentFlag = false;
    else nonResidentFlag = undefined;
  } else if (sameHousehold && isNonSpouseTax) {
    if (d.taxNonResident === 'yes') nonResidentFlag = true;
    else if (d.taxNonResident === 'no') nonResidentFlag = false;
    else nonResidentFlag = Boolean(d.nonResidentFlag);
  } else if (sameHousehold) {
    nonResidentFlag = false;
  }

  const insuranceRequiredBool =
    d.insuranceRequired === '' ? undefined : yes(d.insuranceRequired);
  const registerHealthInsurance =
    d.insuranceRequired === 'yes'
      ? true
      : d.insuranceRequired === 'no'
        ? false
        : d.registerHealthInsurance !== ''
          ? yes(d.registerHealthInsurance)
          : undefined;

  const incomeRaw = str(d.estimatedIncome).replace(/,/g, '');
  const remittanceRaw = str(d.remittanceAmount).replace(/,/g, '');
  const incomeNum = incomeRaw ? Number(incomeRaw) : NaN;
  const remittanceNum = remittanceRaw ? Number(remittanceRaw) : NaN;

  const base: FamilyMemberApi = {
    id: d.id,
    relationship: d.relationship || undefined,
    fullName: str(d.fullName) || undefined,
    fullNameKana: str(d.fullNameKana) || undefined,
    dateOfBirth: str(d.dateOfBirth) || undefined,
    myNumber: str(d.myNumber).replace(/\D/g, '') || undefined,
    disabilityCategory: d.disabilityCategory || undefined,
    disabilityFactsJson: needsDisabilityFacts(d.disabilityCategory)
      ? disabilityFactsToJson(d.disabilityFacts)
      : '',
    livingInSameHousehold: d.livingInSameHousehold === '' ? undefined : yes(d.livingInSameHousehold),
    ifNotAddress: notSameHousehold ? str(d.ifNotAddress) || undefined : undefined,
    nonResidentFlag,
    registerHealthInsurance,
    insuranceRequired: insuranceRequiredBool,
    myNumberLinkStatus: d.myNumberLinkStatus || undefined,
    healthInsuranceDependent:
      d.healthInsuranceDependent !== ''
        ? yes(d.healthInsuranceDependent)
        : d.insuranceRequired === 'yes'
          ? true
          : undefined,
    supportedDependent: d.supportedDependent === '' ? undefined : yes(d.supportedDependent),
    claimedByOtherIncome: d.claimedByOtherIncome === '' ? undefined : yes(d.claimedByOtherIncome),
    otherEarnerName: str(d.otherEarnerName) || undefined,
    otherEarnerAddress: str(d.otherEarnerAddress) || undefined,
    claimTaxDependent: claimTax === '' ? undefined : yes(claimTax),
    taxEligible: undefined,
    taxYear: d.taxYear,
    estimatedIncome: Number.isFinite(incomeNum) ? incomeNum : undefined,
    sharedLivelihood,
    familyWorker: d.spouseFamilyWorker === '' ? undefined : yes(d.spouseFamilyWorker),
    changeDuringYear: d.changeDuringYear === '' ? undefined : yes(d.changeDuringYear),
    withholdingSpouse: d.withholdingSpouse === '' ? undefined : yes(d.withholdingSpouse),
    nonResidentReason: str(d.nonResidentReason) || undefined,
    remittanceAmount: Number.isFinite(remittanceNum) ? remittanceNum : undefined,
    taxChangeReason: str(d.taxChangeReason) || undefined,
    taxChangeDate: str(d.taxChangeDate) || undefined,
    taxCategories: str(d.taxDependentCategory) ? [str(d.taxDependentCategory)] : undefined,
  };

  if (isSpouseTaxFlow(d)) {
    const derived = deriveSpouseWithholding(d);
    const wh = derived.withholdingSpouse as string;
    const te = derived.taxEligible as string;
    base.withholdingSpouse = wh === 'yes' || wh === 'no' ? yes(wh) : undefined;
    base.taxEligible = te === 'yes' || te === 'no' ? yes(te) : undefined;
    base.taxCategories = undefined;
  } else if (isNonSpouseTaxFlow(d)) {
    if (ineligibleReason) {
      base.taxEligible = false;
      if (ineligibleReason === 'no_shared_livelihood') {
        base.sharedLivelihood = false;
      }
    } else {
      const eligible = deriveDependentTaxEligible(d, d.taxYear);
      if (eligible === 'yes' || eligible === 'no') {
        base.taxEligible = yes(eligible);
      } else if (d.taxEligible === 'yes' || d.taxEligible === 'no') {
        base.taxEligible = yes(d.taxEligible);
      }
    }
    base.withholdingSpouse = undefined;
  } else if (claimTax === 'no') {
    base.taxEligible = false;
  } else if (d.taxEligible === 'yes' || d.taxEligible === 'no') {
    base.taxEligible = yes(d.taxEligible);
    base.withholdingSpouse =
      d.withholdingSpouse === '' ? undefined : yes(d.withholdingSpouse);
  }

  return base;
}

export function apiToDraft(m: FamilyMemberApi, taxYear: number): FamilyMemberDraft {
  const yn = (b: boolean | undefined): '' | 'yes' | 'no' => (b === true ? 'yes' : b === false ? 'no' : '');
  const cats = m.taxCategories ?? [];
  const categoryKanji = cats.length > 0 ? cats[0] : '';
  const relationship = (m.relationship as FamilyMemberDraft['relationship']) || '';
  const claimTax = yn(m.claimTaxDependent);
  const isNonSpouseTax = relationship !== 'spouse' && claimTax === 'yes';

  let taxNonResident: FamilyMemberDraft['taxNonResident'] = '';
  if (isNonSpouseTax) {
    if (m.livingInSameHousehold === false) {
      taxNonResident = m.nonResidentFlag === true ? 'yes' : m.nonResidentFlag === false ? 'no' : '';
    } else if (m.nonResidentFlag === true) {
      taxNonResident = 'yes';
    } else if (m.nonResidentFlag === false && (categoryKanji || m.nonResidentReason || m.estimatedIncome != null)) {
      taxNonResident = 'no';
    }
  }

  const taxIneligibilityReason = restoreTaxIneligibilityReason(m, relationship, claimTax, taxYear);
  let sharedLivelihood = yn(m.sharedLivelihood);
  // Older ineligible rows may have eligible_flag=false with shared_livelihood NULL.
  if (!sharedLivelihood && taxIneligibilityReason === 'no_shared_livelihood') {
    sharedLivelihood = 'no';
  }

  const draft: FamilyMemberDraft = {
    id: m.id,
    relationship,
    fullName: m.fullName ?? '',
    fullNameKana: m.fullNameKana ?? '',
    dateOfBirth: m.dateOfBirth ?? '',
    myNumber: m.myNumber ?? '',
    disabilityCategory: (m.disabilityCategory as FamilyMemberDraft['disabilityCategory']) || '',
    disabilityFacts: disabilityFactsFromJson(m.disabilityFactsJson),
    livingInSameHousehold: yn(m.livingInSameHousehold),
    ifNotAddress: m.ifNotAddress ?? '',
    outsideJapan:
      m.livingInSameHousehold === false && relationship === 'spouse'
        ? m.nonResidentFlag === true
          ? 'yes'
          : m.nonResidentFlag === false
            ? 'no'
            : ''
        : '',
    nonResidentFlag: Boolean(m.nonResidentFlag),
    registerHealthInsurance:
      m.registerHealthInsurance === true ? 'yes' : m.registerHealthInsurance === false ? 'no' : '',
    insuranceRequired:
      m.registerHealthInsurance === false
        ? 'no'
        : m.insuranceRequired === true
          ? 'yes'
          : m.insuranceRequired === false
            ? 'no'
            : m.registerHealthInsurance === true
              ? 'yes'
              : '',
    myNumberLinkStatus: (m.myNumberLinkStatus as FamilyMemberDraft['myNumberLinkStatus']) || '',
    healthInsuranceDependent:
      m.healthInsuranceDependent != null
        ? yn(m.healthInsuranceDependent)
        : m.insuranceRequired === true || m.registerHealthInsurance === true
          ? 'yes'
          : '',
    supportedDependent: yn(m.supportedDependent),
    claimedByOtherIncome: yn(m.claimedByOtherIncome),
    otherEarnerName: m.otherEarnerName ?? '',
    otherEarnerAddress: m.otherEarnerAddress ?? '',
    claimTaxDependent: claimTax,
    taxYear: m.taxYear ?? taxYear,
    sharedLivelihood,
    spouseFamilyWorker: yn(m.familyWorker),
    changeDuringYear:
      m.changeDuringYear != null
        ? yn(m.changeDuringYear)
        : m.taxChangeDate || m.taxChangeReason
          ? 'yes'
          : '',
    estimatedIncome: m.estimatedIncome != null ? String(m.estimatedIncome) : '',
    withholdingSpouse: yn(m.withholdingSpouse),
    taxEligible: yn(m.taxEligible),
    taxDependentCategory: categoryKanji,
    taxNonResident,
    taxIneligibilityReason,
    nonResidentReason: m.nonResidentReason ?? '',
    remittanceAmount: m.remittanceAmount != null ? String(m.remittanceAmount) : '',
    taxChangeReason: m.taxChangeReason ?? '',
    taxChangeDate: m.taxChangeDate ?? '',
  };

  if (
    isUnder16AtTaxYearEnd(draft.dateOfBirth, draft.taxYear) &&
    claimTax === 'yes' &&
    !draft.estimatedIncome.trim()
  ) {
    draft.estimatedIncome = '0';
  }

  return draft;
}

/** Re-derive ineligibility after load (reason is not stored as its own DB column). */
function restoreTaxIneligibilityReason(
  m: FamilyMemberApi,
  relationship: string,
  claimTax: '' | 'yes' | 'no',
  taxYear: number
): string {
  if (relationship === 'spouse' || claimTax !== 'yes' || m.taxEligible !== false) return '';
  if (m.sharedLivelihood === false) return 'no_shared_livelihood';
  if (
    incomeOverDependentLimit(
      m.estimatedIncome != null ? String(m.estimatedIncome) : '',
      m.dateOfBirth ?? '',
      m.taxYear ?? taxYear
    )
  ) {
    return 'income_over_limit';
  }
  if (m.nonResidentFlag === true && (m.nonResidentReason ?? '').trim() === 'none') {
    return 'non_resident_no_support';
  }
  return 'no_shared_livelihood';
}

/**
 * Resolve the member row that was just saved — prefer id match, then name/relationship/DOB,
 * never blindly take the last list entry when an id was already known.
 */
export function resolveSavedMemberDraft(
  members: FamilyMemberApi[],
  sent: {
    id?: number | null;
    relationship?: string;
    fullName: string;
    dateOfBirth?: string;
  },
  taxYear: number
): FamilyMemberDraft | null {
  if (!members.length) return null;

  if (sent.id != null) {
    const byId = members.find((m) => m.id === sent.id);
    if (byId) return apiToDraft(byId, taxYear);
  }

  const name = sent.fullName.trim();
  const rel = (sent.relationship ?? '').trim();
  const dob = sent.dateOfBirth ?? '';
  const matches = members.filter((m) => {
    if ((m.fullName ?? '').trim() !== name) return false;
    if (rel && (m.relationship ?? '') !== rel) return false;
    if (dob && (m.dateOfBirth ?? '') !== dob) return false;
    return true;
  });
  if (matches.length > 0) {
    const best = matches.reduce((a, b) => ((a.id ?? 0) >= (b.id ?? 0) ? a : b));
    return apiToDraft(best, taxYear);
  }

  if (sent.id == null) {
    return apiToDraft(members[members.length - 1], taxYear);
  }
  return null;
}

/** Step/status on emp_profile (same as checklist — always use this for progress). */
export async function postFamilyInfoStepProgress(
  email: string,
  familyInfoStep: string,
  familyInfoStatus: string,
  invitationId?: string
): Promise<void> {
  const res = await fetch(`${API_BASE_URL}/api/general-profile`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      email,
      invitationId,
      familyInfoStatus,
      familyInfoStep,
    }),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
}

export async function fetchFamilyInfo(email: string, invitationId?: string): Promise<FamilyInfoProgressResponse> {
  const q = new URLSearchParams({ email });
  if (invitationId) q.set('invitationId', invitationId);
  const res = await fetch(`${API_BASE_URL}/api/family-info?${q}`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

export async function postFamilyInfo(body: {
  email: string;
  invitationId?: string;
  familyInfoStatus?: string;
  familyInfoStep?: string;
  draft?: FamilyMemberDraft;
  saveMember?: FamilyMemberDraft;
  completeSection?: boolean;
}): Promise<FamilyInfoProgressResponse> {
  const payload: Record<string, unknown> = {
    email: body.email,
    invitationId: body.invitationId,
    familyInfoStatus: body.familyInfoStatus,
    familyInfoStep: body.familyInfoStep,
    completeSection: body.completeSection,
  };
  if (body.draft) payload.draft = draftToApi(body.draft);
  if (body.saveMember) payload.saveMember = draftToApi(body.saveMember);
  const res = await fetch(`${API_BASE_URL}/api/family-info`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}
